import { BatchFormFieldType } from 'components/batchForm/BatchForm.helpers';
import {
  handleKeyDownOnClientListField,
  handleKeyDownOnDateField,
  handleKeyDownOnListField,
  handleKeyDownOnLockableField,
  handleKeyDownOnNumericField,
  handleKeyDownOnRadioButtonsField,
  handleKeyDownOnTenorField,
  handleKeyDownOnTextField,
  handleKeyDownOnTimeSpanField,
  handleKeyDownOnToggleField,
  handleKeyDownOnUnderlyingsField,
  handleKeyDownOnVerticalSpotAndGreeksField,
} from 'components/batchForm/BatchFormNavLayer/BatchFormNavLayer.helpers';
import { Locale } from 'date-fns';
import { FieldMetadata } from 'models/forms/fields';
import { Underlying } from 'models/underlying';
import { UnderlyingCodeType } from 'models/underlying/base';
import { KeyboardEvent } from 'react';
import { IntlShape } from 'react-intl';

import { BATCH_WORKSPACE } from './constants';

export interface KeyboardHandlerParams {
  event: KeyboardEvent;
  value: Readonly<BatchFormFieldType | undefined>;
  metadata: FieldMetadata;
  isCellEditable: boolean;
  isPasting: boolean;
  setValue: (value: Readonly<BatchFormFieldType | undefined>) => void;
  copyToClipboard: (value: string, key: string) => void;
  readPastedContentClipboard: () => Promise<string | string[]>;
  cellId: string;
  setPasteCellId: ((cellId: string) => void) | undefined;
  setPastedGridData: ((data: Record<string, string> | undefined) => void) | undefined;
  canEdit: boolean;
  setIsEditing: ((isEditing: boolean) => void) | undefined;
  setIsSelectAll: ((isSelectAll: boolean) => void) | undefined;
  setLostInputBeforeEditing: (value: string) => void;
  lostInputBeforeEditing: string | undefined;
  underlyings: ReadonlyArray<Underlying>;
  intl: IntlShape;
  locale: Locale;
  defaultUnderlyingCodeType: UnderlyingCodeType | undefined;
  setPastedData: ((data: string | undefined) => void) | undefined;
}

export async function handleFieldKeyboardShortcuts(params: KeyboardHandlerParams): Promise<boolean> {
  const {
    event,
    value,
    metadata,
    isCellEditable,
    isPasting,
    setValue,
    copyToClipboard,
    readPastedContentClipboard,
    cellId,
    setPasteCellId,
    setPastedGridData,
    canEdit,
    setIsEditing,
    setIsSelectAll,
    setLostInputBeforeEditing,
    lostInputBeforeEditing,
    underlyings,
    intl,
    locale,
    defaultUnderlyingCodeType,
    setPastedData,
  } = params;

  switch (metadata.type) {
    case 'RadioButtonsField':
      return handleKeyDownOnRadioButtonsField(
        event,
        value,
        isCellEditable,
        metadata,
        isPasting,
        setValue,
        copyToClipboard,
        readPastedContentClipboard,
        cellId,
        setPasteCellId,
        setPastedGridData,
      );
    case 'ToggleField':
      return handleKeyDownOnToggleField(
        event,
        value,
        isCellEditable,
        isPasting,
        setValue,
        copyToClipboard,
        readPastedContentClipboard,
        metadata,
        cellId,
        setPasteCellId,
        setPastedGridData,
      );
    case 'ListField':
    case 'CurrencyListField':
      return handleKeyDownOnListField(
        event,
        value,
        metadata,
        isPasting,
        setValue,
        copyToClipboard,
        readPastedContentClipboard,
        cellId,
        isCellEditable,
        setPasteCellId,
        setPastedGridData as ((data: Record<string, string>) => void) | undefined,
      );
    case 'LockablePercentField':
      return handleKeyDownOnLockableField(
        event,
        value,
        canEdit,
        setValue,
        setIsEditing,
        setIsSelectAll,
        (lostInput: string | undefined) => setLostInputBeforeEditing(lostInput ?? ''),
        BATCH_WORKSPACE,
        metadata,
      );
    case 'MultipleUnderlyingsField':
    case 'UnderlyingsField':
      return handleKeyDownOnUnderlyingsField(
        event,
        value,
        isPasting,
        setValue,
        setIsEditing,
        copyToClipboard,
        readPastedContentClipboard,
        (lostInput: string | undefined) => {
          setLostInputBeforeEditing(lostInput ?? '');
        },
        BATCH_WORKSPACE,
        (e: KeyboardEvent) => e.key === 'Delete',
        canEdit,
        setPastedData,
      );
    case 'ClientListField':
      return handleKeyDownOnClientListField(
        event,
        value,
        isPasting,
        lostInputBeforeEditing,
        setValue,
        setIsEditing,
        copyToClipboard,
        readPastedContentClipboard,
        (lostInput: string | undefined) => setLostInputBeforeEditing(lostInput ?? ''),
        BATCH_WORKSPACE,
        (e: KeyboardEvent) => e.key === 'Delete',
        canEdit,
      );
    case 'DateField':
      return handleKeyDownOnDateField(
        event,
        value,
        isPasting,
        setValue,
        copyToClipboard,
        readPastedContentClipboard,
        metadata,
        cellId,
        canEdit,
        setPasteCellId,
        setPastedGridData,
      );
    case 'AmountField':
    case 'NumberField':
    case 'PercentField':
      return handleKeyDownOnNumericField(
        event,
        metadata,
        value,
        isPasting,
        setValue,
        copyToClipboard,
        readPastedContentClipboard,
        cellId,
        setPasteCellId,
        setPastedGridData as ((data: Record<string, string>) => void) | undefined,
      );
    case 'CustomizableTenorField':
      return handleKeyDownOnTenorField(
        event,
        value,
        isPasting,
        setValue,
        copyToClipboard,
        readPastedContentClipboard,
        cellId,
        setPasteCellId,
        setPastedGridData,
      );
    case 'TextField':
    case 'ExternalQuoteIdField':
      return handleKeyDownOnTextField(event, value, copyToClipboard);
    case 'TimeSpanField':
      return handleKeyDownOnTimeSpanField(event, value, copyToClipboard, locale);
    case 'SpotAndGreeksField':
      if (defaultUnderlyingCodeType) {
        handleKeyDownOnVerticalSpotAndGreeksField(
          event,
          value,
          underlyings,
          copyToClipboard,
          intl,
          defaultUnderlyingCodeType,
        );
      }
      return true;
    case 'ScheduleModalField':
    case 'DealSplitModalField':
      // Shortcuts are not enabled for Schedule Dates field
      return true;
    default:
      return false;
  }
}
